import { AxiosResponse } from 'axios';

import { ApiResponseDto } from 'interfaces/apiResponse';

/**
 * HTTP status the backend uses when the ERGANI system itself rejected or failed the request
 * (see `ErganiException` -> `Status.BAD_GATEWAY`).
 */
export const ERGANI_ERROR_STATUS = 502;

/**
 * Error raised when ERGANI rejected a request. ERGANI has no stable error codes, so the message
 * coming from the backend is the only meaningful thing we can show to the user.
 *
 * Backend validation rejections are wrapped in it as well, carrying the first backend `code`
 * (for translation), the HTTP `status`, and `fromErgani` (true only when ERGANI itself — 502 —
 * did the rejecting).
 */
export class ErganiApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
    readonly status?: number,
    readonly fromErgani = false,
  ) {
    super(message);
    this.name = 'ErganiApiError';
  }
}

export const isErganiApiError = (error: unknown): error is ErganiApiError => error instanceof ErganiApiError;

/**
 * Returns the ERGANI-provided message when the error originated from ERGANI, otherwise the fallback.
 */
export const getErganiErrorMessage = (error: unknown, fallback: string): string =>
  isErganiApiError(error) && error.message ? error.message : fallback;

export const joinApiErrors = (errors: ApiResponseDto<unknown>['errors']): string | undefined => {
  const message = (errors ?? [])
    .map(error => error.message)
    .filter(Boolean)
    .join(', ');

  return message || undefined;
};

export interface HttpErrorLike {
  response?: { status?: number; data?: ApiResponseDto<unknown> };
}

/**
 * Re-throws request errors so that ERGANI failures (502) surface with the backend message.
 */
export const rethrowErganiError = (error: unknown): never => {
  const response = (error as HttpErrorLike | undefined)?.response;

  if (response?.status === ERGANI_ERROR_STATUS) {
    const message = joinApiErrors(response.data?.errors);

    if (message) {
      throw new ErganiApiError(message, response.data?.errors?.[0]?.code, response.status, true);
    }
  }

  throw error;
};

/**
 * Converts a request failure into an `ErganiApiError`, preserving the first backend error code,
 * the HTTP status, and whether ERGANI itself (502) was the rejector. Unlike
 * `rethrowErganiError` it never re-throws the raw axios error: the UI always gets a message
 * plus, where present, a status / code it can translate.
 */
export const toErganiApiError = (error: unknown): ErganiApiError => {
  const response = (error as HttpErrorLike | undefined)?.response;
  const message = joinApiErrors(response?.data?.errors) ?? (error instanceof Error ? error.message : String(error));

  return new ErganiApiError(message, response?.data?.errors?.[0]?.code, response?.status, response?.status === ERGANI_ERROR_STATUS);
};

/**
 * Executes a request against an ERGANI-related endpoint and unwraps the `ApiResponseDto`.
 */
export const erganiRequest = async <T>(request: () => Promise<AxiosResponse<ApiResponseDto<T>>>): Promise<T> => {
  let response: AxiosResponse<ApiResponseDto<T>>;

  try {
    response = await request();
  } catch (error) {
    return rethrowErganiError(error);
  }

  if (response.data.errors) {
    throw new ErganiApiError(
      joinApiErrors(response.data.errors) ?? 'Request returned errors',
      response.data.errors[0]?.code,
      response.status,
      response.status === ERGANI_ERROR_STATUS,
    );
  }

  return response.data.data as T;
};
